import React from 'react';
import { BadgeCheck, Printer, ShieldCheck } from 'lucide-react';

// Texto del contrato + certificado de firmas. Lo usan el panel del criador y la página del comprador.
// "Imprimir / guardar PDF" usa el diálogo del navegador (los estilos de impresión ocultan el resto de la página).
const fmt = (d?: string | null) => (d ? new Date(String(d).replace(' ', 'T')).toLocaleString('es-CL', { dateStyle: 'long', timeStyle: 'short' }) : '—');

export const ContractDocument: React.FC<{ c: any; showPrint?: boolean }> = ({ c, showPrint = true }) => {
    const signed = c.status === 'signed';
    return (
        <div className="contract-doc bg-white rounded-[1.5rem] border border-slate-200 shadow-sm overflow-hidden">
            <style>{`@media print { body * { visibility: hidden !important; } .contract-doc, .contract-doc * { visibility: visible !important; } .contract-doc { position: absolute; left: 0; top: 0; width: 100%; border: 0; box-shadow: none; } }`}</style>
            <div className="px-6 sm:px-10 py-8 border-b border-slate-100">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.25em] mb-2">Contrato de compraventa · {c.kennelName}</p>
                <h2 className="text-2xl sm:text-3xl font-black text-slate-900 leading-tight">Compraventa de {c.petName}</h2>
                {signed && <p className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-black uppercase tracking-widest"><BadgeCheck size={14} /> Firmado por ambas partes</p>}
            </div>

            <div className="px-6 sm:px-10 py-8 space-y-6">
                {c.content.map((s: any, i: number) => (
                    <section key={i}>
                        <h3 className="text-[12px] font-black text-slate-800 uppercase tracking-widest mb-2">{s.title}</h3>
                        {String(s.text).split('\n').map((line, j) => <p key={j} className="text-[14px] text-slate-600 leading-relaxed mb-1.5">{line}</p>)}
                    </section>
                ))}
            </div>

            {/* Firmas */}
            <div className="px-6 sm:px-10 py-8 bg-slate-50 border-t border-slate-100 grid sm:grid-cols-2 gap-6">
                {[
                    { label: 'Vendedor', name: c.breederSigner, at: c.breederSignedAt, ip: c.breederIp, sig: c.breederSignature },
                    { label: 'Comprador', name: c.buyerSigner, at: c.buyerSignedAt, ip: c.buyerIp, sig: c.buyerSignature },
                ].map(p => (
                    <div key={p.label} className="bg-white rounded-[7px] border border-slate-200 p-5">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">{p.label}</p>
                        {p.at ? (
                            <>
                                {p.sig ? <img src={p.sig} alt={`Firma ${p.label}`} className="h-16 object-contain mb-2" /> : <p className="font-serif italic text-2xl text-slate-700 mb-2">{p.name}</p>}
                                <p className="text-[13px] font-bold text-slate-700">{p.name}</p>
                                <p className="text-[11px] text-slate-400 font-medium">{fmt(p.at)}{p.ip ? ` · IP ${p.ip}` : ''}</p>
                            </>
                        ) : <p className="text-[12px] font-bold text-slate-300 uppercase tracking-widest py-6">Pendiente de firma</p>}
                    </div>
                ))}
            </div>

            {/* Certificado */}
            <div className="px-6 sm:px-10 py-6 border-t border-slate-100">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-2"><ShieldCheck size={13} /> Registro de firma electrónica simple (Ley 19.799)</p>
                <p className="text-[11px] text-slate-500 leading-relaxed">Sello del contenido (SHA-256): <span className="font-mono break-all">{c.contentHash || '—'}</span></p>
                {signed && <p className="text-[11px] text-slate-500 leading-relaxed mt-1">Sello del contrato firmado: <span className="font-mono break-all">{c.finalHash}</span></p>}
                <p className="text-[10px] text-slate-400 leading-relaxed mt-2">La identidad del comprador se verificó con un código enviado a su correo. Cualquier cambio al texto o a las firmas altera estos sellos.</p>
            </div>

            {showPrint && (
                <div className="px-6 sm:px-10 pb-8 print:hidden">
                    <button onClick={() => window.print()} className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 hover:bg-orange-500 text-white rounded-full text-[11px] font-black uppercase tracking-widest transition-colors"><Printer size={14} /> Imprimir / guardar PDF</button>
                </div>
            )}
        </div>
    );
};

export default ContractDocument;
